import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import {
  type Board,
  boardColor,
  type CalendarOccurrence,
  type CalendarScope,
  type DeadlineValue,
  scopeKey,
  useCalendar,
  useChangeDeadline,
} from '@/entities/board';
import { useQuickCreateDefaults } from '@/entities/workspace';
import { useErrorMessage } from '@/shared/i18n';
import { todayIn } from '@/shared/lib/calendar';
import { useIsMobile, useMediaQuery } from '@/shared/lib/media';
import { Dialog, ErrorState, LoadingState, useToast } from '@/shared/ui';

import { Agenda } from './Agenda';
import { EventPreview } from './EventPreview';
import styles from './CalendarView.module.css';
import { CalendarFilters } from './CalendarFilters';
import { CalendarToolbar } from './CalendarToolbar';
import { CompactWeek } from './CompactWeek';
import { CreateEventDialog } from './CreateEventDialog';
import { DaySchedule } from './DaySchedule';
import { OccurrenceDialog } from './OccurrenceDialog';
import { useCalendarPrefs } from './prefs';
import { useSharedSources } from './sources';
import { ViewOptions } from './ViewOptions';
import { TaskPanel } from './TaskPanel';
import { TextCreateButton } from './TextCreateButton';
import { TextEventDialog } from './TextEventDialog';
import {
  type CalendarEvent,
  DEFAULT_WORKWEEK,
  defaultCalendarView,
  draftFromDays,
  eachDay,
  filterByKind,
  gridEvents,
  hasDensity,
  localNow,
  shiftDate,
  taskGroups,
  toEvents,
  toOccurrenceEvents,
  toUndated,
  visibleRange,
  withoutPast,
  workweekDates,
  withPhases,
  workloadByDay,
  type CreateDraft,
} from './model';
import { useCalendarState } from './useCalendarState';

// The calendar library is a separate chunk, loaded when a calendar is first shown.
const CalendarGrid = lazy(() => import('./CalendarGrid'));

const NEUTRAL = '#8a94a6';
const TABLET_QUERY = '(max-width: 1100px)';

interface Props {
  scope: CalendarScope;
  /**
   * The boards in scope: just the current one on a board, all boards of the workspace otherwise.
   * Unused by the personal calendar (`me`), whose sources come with its data.
   */
  boards: readonly Board[];
  /** The user may create and change tasks (editor or owner). */
  canEdit: boolean;
  /** 0 = Monday ... 6 = Sunday (account preference). */
  firstDay: number;
  /** IANA timezone of the account. */
  timeZone: string | null;
  locale: string;
  /**
   * ISO weekdays (1 = Monday ... 7) of the Work week. Only a board calendar passes its board's
   * setting; workspace and planner calendars keep Monday - Friday.
   */
  workweekDays?: readonly number[];
  /** Workspace names by id: the global calendar groups its board filter by workspace and names it in lists. */
  workspaceNames?: ReadonlyMap<string, string>;
  /** `boardId` is the board the card lives on (the planner spans boards). */
  onOpenCard: (cardId: string, boardId: string) => void;
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebounced(value);
    }, delay);
    return () => {
      window.clearTimeout(timer);
    };
  }, [value, delay]);
  return debounced;
}

/** The current wall-clock minute in a zone; re-read every 30 s so "past" and "now" stay true. */
function useNowLocal(timeZone: string | null): string {
  const [state, setState] = useState(() => ({ timeZone, now: localNow(timeZone) }));
  useEffect(() => {
    const timer = window.setInterval(() => {
      setState({ timeZone, now: localNow(timeZone) });
    }, 30_000);
    return () => {
      window.clearInterval(timer);
    };
  }, [timeZone]);
  // Another zone (the profile changed): read the clock again at once.
  return state.timeZone === timeZone ? state.now : localNow(timeZone);
}

/**
 * One calendar for both scopes: the cards with deadlines of a board, or of the (chosen) boards
 * of a workspace. It is only a view of existing cards: nothing here is stored besides the
 * cards' own deadlines. View, date and filters are kept in the URL.
 */
export function CalendarView({
  scope,
  boards,
  canEdit,
  firstDay,
  timeZone,
  locale,
  workweekDays = DEFAULT_WORKWEEK,
  workspaceNames,
  onOpenCard,
}: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const getErrorMessage = useErrorMessage();
  const mobile = useIsMobile();
  const tablet = useMediaQuery(TABLET_QUERY);
  const [params, setParams] = useSearchParams();
  const today = todayIn(timeZone);
  const workspace = scope.kind === 'workspace';
  // Workspace and global calendars span several boards: the destination of a new event is always
  // an explicit choice among the boards where the viewer may create cards.
  const aggregate = workspace || scope.kind === 'all';
  const personal = scope.kind === 'me';
  // Switch preferences are remembered per board / per workspace (never shared between them).
  const storageKey = scopeKey(
    scope.kind === 'board'
      ? { view: 'calendar', kind: 'board', id: scope.boardId }
      : scope.kind === 'workspace'
        ? { view: 'calendar', kind: 'workspace', id: scope.workspaceId }
        : scope.kind === 'all'
          ? { view: 'calendar', kind: 'all', id: 'global' }
          : { view: 'calendar', kind: 'me', id: 'planner' },
  );
  // The view a link does not name: the one last chosen on THIS kind of device, else a sensible
  // default (a phone starts on its day, the planner on the month, boards on the week).
  const device = mobile ? 'phone' : 'desktop';
  const defaults = useMemo(
    () => defaultCalendarView(storageKey, device, personal),
    [storageKey, device, personal],
  );
  // Only the GLOBAL calendar shares its boards (with the Home calendar); the workspace calendar
  // keeps its own, and board / planner calendars have none.
  const shared = useSharedSources();
  const known = useMemo(() => new Set(boards.map((board) => board.id)), [boards]);
  const sharedSources = useMemo(
    () => (scope.kind === 'all' ? { ...shared, known } : undefined),
    [scope.kind, shared, known],
  );
  const [state, update] = useCalendarState(params, setParams, today, storageKey, {
    defaults,
    device,
    ...(sharedSources && { sources: sharedSources }),
  });
  const [creating, setCreating] = useState<CreateDraft | null>(null);
  const [textCreating, setTextCreating] = useState(false);
  const [dayDetails, setDayDetails] = useState<string | null>(null);
  const [occurrence, setOccurrence] = useState<CalendarOccurrence | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [prefs, setPrefs] = useCalendarPrefs(storageKey);
  const nowLocal = useNowLocal(timeZone);
  const changeDeadline = useChangeDeadline();

  const quickDefaults = useQuickCreateDefaults(
    scope.kind === 'workspace' ? scope.workspaceId : '',
    scope.kind === 'workspace',
  );
  const isDefaultWorkweek =
    workweekDays.length === DEFAULT_WORKWEEK.length &&
    workweekDays.every((day, index) => day === DEFAULT_WORKWEEK[index]);
  const range = visibleRange(state.mode, state.date, firstDay, workweekDays);
  // The shown days of the Work week (a selection may have gaps); every other mode shows the range.
  const shownDays = useMemo(
    () =>
      state.mode === 'workweek'
        ? workweekDates(state.date, firstDay, workweekDays)
        : eachDay(visibleRange(state.mode, state.date, firstDay)),
    [state.mode, state.date, firstDay, workweekDays],
  );
  // Work week shows five days but asks for the whole week: weekend tasks are never dropped from
  // the data, they are announced and one click (or the Week view) away.
  const fetchRange = state.mode === 'workweek' ? visibleRange('week', state.date, firstDay) : range;
  const shownBoards = useMemo(
    () => boards.filter((board) => !state.hiddenBoardIds.includes(board.id)),
    [boards, state.hiddenBoardIds],
  );
  const search = useDebounced(state.search, 300);
  const query = {
    start: fetchRange.start,
    end: fetchRange.end,
    // The planner lists active tasks only; completed ones are a separate filter of its list.
    includeCompleted: personal ? false : state.includeCompleted,
    includeOverdue: state.includeOverdue,
    // Archived tasks are on by default (the server's default); only an explicit "off" is sent.
    includeArchived: personal ? true : prefs.showArchived,
    includeUndated: state.showTasks,
    search,
    labelIds: state.labelIds,
    // Everything visible = no restriction (also covers boards created meanwhile).
    boardIds:
      aggregate && shownBoards.length < boards.length ? shownBoards.map((board) => board.id) : [],
  };
  const nothingShown = aggregate && shownBoards.length === 0;
  const calendar = useCalendar(scope, query, !nothingShown);
  // Where an event can be created or moved: the server says which boards allow it. (A response
  // without the list - an older server - falls back to every shown board; the server still checks.)
  const creatableIds = calendar.data?.creatable_board_ids;
  const creatableBoards = useMemo(
    () =>
      aggregate
        ? shownBoards.filter((board) => !creatableIds || creatableIds.includes(board.id))
        : [...boards],
    [aggregate, boards, shownBoards, creatableIds],
  );
  const editableBoards = useMemo(
    () => (aggregate && creatableIds ? new Set(creatableIds) : undefined),
    [aggregate, creatableIds],
  );

  // Per board: an aggregated calendar shows read-only boards too, whose events are never editable.
  const canEditBoard = (boardId: string): boolean =>
    canEdit && (!editableBoards || editableBoards.has(boardId));

  // Every event of the range with its derived phase (filters applied). The workload is computed
  // from these: hiding past events is a presentation choice and must not change a day's load.
  const filteredEvents = useMemo(
    () =>
      nothingShown
        ? []
        : withPhases(
            filterByKind(
              [
                ...toEvents(calendar.data?.cards ?? []),
                ...toOccurrenceEvents(calendar.data?.occurrences ?? []),
              ].filter((event) => !state.hiddenBoardIds.includes(event.boardId)),
              state,
            ),
            nowLocal,
          ),
    [calendar.data, nothingShown, state, nowLocal],
  );
  const allEvents = useMemo(
    () => (prefs.hidePast ? withoutPast(filteredEvents) : filteredEvents),
    [filteredEvents, prefs.hidePast],
  );
  const undated = useMemo(
    () =>
      nothingShown || !state.showTasks
        ? []
        : toUndated(calendar.data?.undated ?? []).filter(
            (task) => !state.hiddenBoardIds.includes(task.boardId),
          ),
    [calendar.data, nothingShown, state.showTasks, state.hiddenBoardIds],
  );
  const labels = calendar.data?.labels ?? [];
  const events = useMemo(() => gridEvents(allEvents, state.mode), [allEvents, state.mode]);
  // Tasks of the weekend days a Work week does not show.
  const weekendCount = useMemo(() => {
    if (state.mode !== 'workweek') return 0;
    const shown = new Set(shownDays);
    return allEvents.filter((event) => {
      const day = event.start.slice(0, 10);
      return day >= fetchRange.start && day <= fetchRange.end && !shown.has(day);
    }).length;
  }, [allEvents, state.mode, shownDays, fetchRange.start, fetchRange.end]);
  const { start: rangeStart, end: rangeEnd } = range;
  const workload = useMemo(
    () =>
      prefs.workload
        ? workloadByDay(filteredEvents, eachDay({ start: rangeStart, end: rangeEnd }))
        : null,
    [filteredEvents, prefs.workload, rangeStart, rangeEnd],
  );
  const groups = useMemo(
    () => taskGroups(allEvents, undated, { start: rangeStart, end: rangeEnd }),
    [allEvents, undated, rangeStart, rangeEnd],
  );

  // The personal calendar learns its boards from its own data (only those the viewer's tasks
  // come from); the others are given them.
  const sources = useMemo(
    () => [...boards, ...(calendar.data?.boards ?? [])],
    [boards, calendar.data?.boards],
  );
  const colors = useMemo(
    () => new Map(sources.map((board) => [board.id, boardColor(board)])),
    [sources],
  );
  const names = useMemo(() => new Map(sources.map((board) => [board.id, board.name])), [sources]);
  const sourceNames = useMemo(
    () =>
      new Map(
        (calendar.data?.boards ?? []).map((board) => [
          board.id,
          [board.name, board.workspace_name].filter(Boolean).join(' · '),
        ]),
      ),
    [calendar.data?.boards],
  );
  const colorOf = useCallback((boardId: string) => colors.get(boardId) ?? NEUTRAL, [colors]);
  const boardNameOf = useCallback((boardId: string) => names.get(boardId) ?? '', [names]);
  // Where an item comes from, spelled out where items of several boards share a view.
  const sourceOf = useCallback(
    (boardId: string) =>
      scope.kind === 'board' ? '' : (sourceNames.get(boardId) ?? names.get(boardId) ?? ''),
    [scope.kind, sourceNames, names],
  );

  // Opening an item shows a compact preview first; the card editor (or, for an upcoming repeat,
  // its own editor) is one action away.
  const openEvent = (id: string) => {
    setPreviewId(id);
  };
  const previewEvent = previewId
    ? allEvents.find((candidate) => candidate.id === previewId)
    : undefined;
  const previewUndated = previewId ? undated.find((task) => task.id === previewId) : undefined;
  const previewItem = previewEvent
    ? { event: previewEvent }
    : previewUndated
      ? { undated: previewUndated }
      : null;
  const previewBoardId = previewEvent?.boardId ?? previewUndated?.boardId ?? '';
  const openFullCard = () => {
    const id = previewId;
    setPreviewId(null);
    if (id) onOpenCard(id, previewBoardId);
  };

  const apply = (cardId: string, value: DeadlineValue) => {
    if (allEvents.some((event) => event.id === cardId && event.occurrence)) return; // not a card
    const current = allEvents.find((event) => event.id === cardId)?.card.deadline;
    if (
      current &&
      current.date === value.date &&
      (current.time?.slice(0, 5) ?? null) === value.time &&
      current.start_date === value.start_date &&
      (current.start_time?.slice(0, 5) ?? null) === value.start_time
    ) {
      return; // dropped where it already was
    }
    changeDeadline.mutate(
      { cardId, deadline: value },
      {
        onError: (error) => {
          toast.show({ message: getErrorMessage(error) });
        },
      },
    );
  };

  const openCreate = (draft: CreateDraft) => {
    if (canEdit && creatableBoards.length > 0) setCreating(draft);
  };
  const openDayCreate = (day: string) => {
    setDayDetails(null);
    openCreate(draftFromDays(day, null));
  };

  // Presentations: a phone's day is a schedule, its other non-month views are a list of days; a
  // desktop's Week / Work week are compact day columns unless the hour grid is chosen.
  const daySchedule = mobile && state.mode === 'day';
  const agenda = mobile && state.mode !== 'month' && !daySchedule;
  const compactWeek = !mobile && hasDensity(state.mode) && state.density === 'compact';
  const filters = (
    <CalendarFilters
      state={state}
      labels={labels}
      hideCompleted={personal}
      {...(!personal && {
        showArchived: prefs.showArchived,
        onShowArchived: (show: boolean) => {
          setPrefs({ showArchived: show });
        },
      })}
      {...(aggregate && { boards, ...(workspaceNames && { workspaceNames }) })}
      onChange={update}
    />
  );

  // Compact week and the day schedule already list every dated task: the panel keeps only the
  // tasks without a date.
  const onlyUndated = compactWeek || daySchedule;
  const panel = (
    <TaskPanel
      groups={onlyUndated ? { dated: [], undated: groups.undated } : groups}
      labels={labels}
      colorOf={colorOf}
      boardNameOf={boardNameOf}
      draggable={canEdit}
      collapsible={tablet}
      locale={locale}
      onOpenCard={openEvent}
    />
  );

  return (
    <div className={styles.view} data-testid="calendar-view" data-mode={state.mode}>
      <CalendarToolbar
        mode={state.mode}
        density={state.density}
        showDensity={!mobile && hasDensity(state.mode)}
        date={state.date}
        range={range}
        today={today}
        firstDay={firstDay}
        locale={locale}
        onMode={(mode) => {
          update({ mode });
        }}
        onDensity={(density) => {
          update({ density });
        }}
        onDate={(date) => {
          update({ date });
        }}
        onPrevious={() => {
          update({ date: shiftDate(state.mode, state.date, -1) });
        }}
        onNext={() => {
          update({ date: shiftDate(state.mode, state.date, 1) });
        }}
      >
        {filters}
        {canEdit && creatableBoards.length > 0 && (
          <TextCreateButton
            onClick={() => {
              setTextCreating(true);
            }}
          />
        )}
        <ViewOptions prefs={prefs} onChange={setPrefs} />
      </CalendarToolbar>

      <div className={styles.body}>
        <div className={styles.stage}>
          {calendar.isError && (
            <ErrorState error={calendar.error} onRetry={() => void calendar.refetch()} />
          )}
          {nothingShown && <p className={styles.note}>{t('calendar.noBoardsShown')}</p>}
          {weekendCount > 0 && (
            <p className={styles.note} role="status" data-testid="weekend-note">
              {t(isDefaultWorkweek ? 'calendar.weekendTasks' : 'calendar.offDayTasks', {
                count: weekendCount,
              })}{' '}
              <button
                type="button"
                className={styles.noteAction}
                onClick={() => {
                  update({ mode: 'week' });
                }}
              >
                {t('calendar.showFullWeek')}
              </button>
            </p>
          )}
          {calendar.isPending && !nothingShown && <LoadingState />}
          {(agenda || daySchedule) && !calendar.isPending && panel}
          {!calendar.isPending || nothingShown ? (
            daySchedule ? (
              <DaySchedule
                date={state.date}
                events={allEvents}
                today={today}
                firstDay={firstDay}
                locale={locale}
                timeZone={timeZone}
                colorOf={colorOf}
                sourceOf={sourceOf}
                labels={labels}
                workload={workload}
                onSelectDate={(date) => {
                  update({ date });
                }}
                onOpenCard={openEvent}
              />
            ) : compactWeek ? (
              <div className={styles.compactArea}>
                <CompactWeek
                  days={shownDays}
                  events={allEvents}
                  today={today}
                  locale={locale}
                  colorOf={colorOf}
                  sourceOf={sourceOf}
                  labels={labels}
                  workload={workload}
                  onOpenCard={openEvent}
                />
                {panel}
              </div>
            ) : agenda ? (
              <Agenda
                events={allEvents}
                range={range}
                days={shownDays}
                today={today}
                locale={locale}
                colorOf={colorOf}
                boardNameOf={boardNameOf}
                labels={labels}
                workload={workload}
                {...(canEdit && { onCreateDay: openDayCreate })}
                onOpenCard={openEvent}
              />
            ) : (
              <Suspense fallback={<LoadingState />}>
                <CalendarGrid
                  mode={state.mode}
                  workDays={shownDays}
                  date={state.date}
                  firstDay={firstDay}
                  timeZone={timeZone}
                  locale={locale}
                  events={events}
                  allEvents={allEvents}
                  undated={undated}
                  labels={labels}
                  colorOf={colorOf}
                  boardNameOf={boardNameOf}
                  editable={canEdit}
                  compact={mobile}
                  {...(editableBoards && { editableBoards })}
                  workload={workload}
                  panel={panel}
                  onOpenCard={openEvent}
                  onChangeDeadline={(event: CalendarEvent, value: DeadlineValue) => {
                    apply(event.id, value);
                  }}
                  onMoveDeadline={apply}
                  onCreate={openCreate}
                  onPickDay={setDayDetails}
                />
              </Suspense>
            )
          ) : null}
        </div>
      </div>

      {creating && (
        <CreateEventDialog
          boards={creatableBoards}
          {...(workspaceNames && { workspaceNames })}
          chooseBoard={aggregate}
          draft={creating}
          timezone={timeZone}
          memoryKey={
            scope.kind === 'board'
              ? scope.boardId
              : workspace
                ? scope.workspaceId
                : scope.kind === 'all'
                  ? 'all'
                  : 'me'
          }
          quickDefaults={quickDefaults.data ?? null}
          onClose={() => {
            setCreating(null);
          }}
        />
      )}
      {previewItem && !occurrence && (
        <EventPreview
          item={previewItem}
          source={sourceOf(previewBoardId) || boardNameOf(previewBoardId)}
          workspaceId={sources.find((board) => board.id === previewBoardId)?.workspace_id ?? null}
          locale={locale}
          canEdit={canEditBoard(previewBoardId)}
          onClose={() => {
            setPreviewId(null);
          }}
          onOpenCard={openFullCard}
          onEditOccurrence={() => {
            setOccurrence(previewEvent?.occurrence ?? null);
            setPreviewId(null);
          }}
          onOpenSeriesSource={(cardId) => {
            setPreviewId(null);
            onOpenCard(cardId, previewBoardId);
          }}
        />
      )}
      {textCreating && (
        <TextEventDialog
          boards={creatableBoards}
          {...(workspaceNames && { workspaceNames })}
          chooseBoard={aggregate}
          requireBoardChoice={aggregate}
          timezone={timeZone}
          memoryKey={
            scope.kind === 'board'
              ? scope.boardId
              : workspace
                ? scope.workspaceId
                : scope.kind === 'all'
                  ? 'all'
                  : 'me'
          }
          quickDefaults={quickDefaults.data ?? null}
          onClose={() => {
            setTextCreating(false);
          }}
        />
      )}
      {occurrence && (
        <OccurrenceDialog
          occurrence={occurrence}
          canEdit={canEditBoard(occurrence.board_id)}
          onClose={() => {
            setOccurrence(null);
          }}
          onOpenCard={(cardId) => {
            setOccurrence(null);
            onOpenCard(cardId, occurrence.board_id);
          }}
        />
      )}
      {dayDetails && (
        <Dialog
          title={t('calendar.dayDetails')}
          onClose={() => {
            setDayDetails(null);
          }}
        >
          <Agenda
            events={allEvents}
            range={{ start: dayDetails, end: dayDetails }}
            today={today}
            locale={locale}
            colorOf={colorOf}
            boardNameOf={boardNameOf}
            labels={labels}
            {...(canEdit && { onCreateDay: openDayCreate })}
            onOpenCard={(id) => {
              setDayDetails(null);
              openEvent(id);
            }}
          />
        </Dialog>
      )}
    </div>
  );
}
